import React, { useCallback, useEffect, useState } from "react";
import Page from "../../Components/Page";
import FetchHook from "../../Api/FetchHooks";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import moment from "moment";
import {
  AccountBalance,
  CalendarToday,
  Copyright,
  CurrencyExchange,
  Description,
  Info,
  MonetizationOn,
  Percent,
  Savings,
  SettingsSuggest,
  VerifiedUser,
} from "@mui/icons-material";
import PageLoader from "../../Components/PageLoader";

const Setting = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, response, request] = FetchHook();

  const fetchSettings = useCallback(async () => {
    await request({ url: `/system_setting` });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (response && response.data?.Settings) {
      setSettings(response.data.Settings);
    }
  }, [response]);

  const formatDate = (date) =>
    date ? moment(date).format("DD MMM YYYY, hh:mm A") : "N/A";

  const renderCard = (title, icon, items, color) => (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          height: "100%",
          borderRadius: 3,
          // borderBottom: `4px solid ${color}`,
          // boxShadow: `0 12px 24px -4px ${alpha(color, 0.2)}`,
        }}
      >
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: alpha(color, 0.1),
                  color: color,
                }}
              >
                {icon}
              </Box>
              <Typography variant="h6" fontWeight="bold">
                {title}
              </Typography>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            {items.map((item, index) => (
              <Grid item xs={6} key={index}>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="subtitle1" fontWeight="medium">
                  {item.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Page title={"System Settings"}>
      {loading ? (
        <PageLoader />
      ) : settings ? (
        <Grid container spacing={3}>
          {/* General Status */}
          {renderCard(
            "General Information",
            <Info />,
            [
              {
                label: "Platform Owner ID",
                value: settings.platform_owner_id,
              },
              {
                label: "Status",
                value: (
                  <Chip
                    label={settings.active_status}
                    color={
                      settings.active_status === "active" ? "success" : "error"
                    }
                    size="small"
                  />
                ),
              },
              { label: "Start Date", value: formatDate(settings.start_date) },
              {
                label: "Last Updated",
                value: formatDate(settings.last_updated_on),
              },
            ],
            theme.palette.info.main
          )}

          {/* Shares Configuration */}
          {renderCard(
            "Shares Configuration",
            <CurrencyExchange />,
            [
              {
                label: "Minimum Share",
                value: settings.minimum_share,
              },
              {
                label: "Share Value",
                value: settings.sharevalue,
              },
              {
                label: "Max Sell Shares (%)",
                value: `${settings.maximum_sell_shares_p}%`,
              },
              {
                label: "Min Sell Shares (%)",
                value: `${settings.minimum_sell_shares_p}%`,
              },
            ],
            theme.palette.primary.main
          )}

          {/* Pool & Savings */}
          {renderCard(
            "Pool & Savings",
            <Savings />,
            [
              {
                label: "Minimum Pool Saving",
                value: settings.minimum_pool_saving,
              },
              {
                label: "Max Pool Contribution",
                value: settings.maximun_pool_contribution,
              },
            ],
            theme.palette.success.main
          )}

          {/* Fees & Charges */}
          {renderCard(
            "Fees & Charges",
            <MonetizationOn />,
            [
              {
                label: "Registration Fees",
                value: settings.registration_fees,
              },
              {
                label: "IPRS Value",
                value: settings.iprs_value,
              },
              {
                label: "Simu Loan",
                value: settings.simu_laon || "N/A",
              },
            ],
            theme.palette.warning.main
          )}
        </Grid>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <Typography color="text.secondary">
            No settings data available.
          </Typography>
        </Box>
      )}
    </Page>
  );
};

export default Setting;
